import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { OrderService } from '@services/order.service';
import { OrderEditDialogComponent } from '@components/admin/patient/order-edit-dialog/order-edit-dialog.component';
import { CustomerNotifyDialogComponent } from '@components/admin/patient/customer-notify-dialog.component';
import {
  jobReadyBoardStatus,
  jobReadySortRank,
} from '@classes/job-ready';

@Component({
  selector: 'app-managed-board-job-ready',
  templateUrl: './managed-board-job-ready.component.html',
  styleUrls: [
    '../managed-board.component.css',
    './managed-board-job-ready.component.css',
  ],
})
export class ManagedBoardJobReadyComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;
  displayedColumns = [
    'alert',
    'status',
    'vendor',
    'model',
    'color',
    'group',
    'patient',
    'invoiceNumber',
    'notify',
    'log',
  ];
  orders: any[] = [];
  dataSource = new MatTableDataSource<any>([]);
  dataLoading = false;

  constructor(
    private orderService: OrderService,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.dataSource.sortingDataAccessor = (row, property) => {
      if (property === 'status') {
        return jobReadySortRank(this.statusLabel(row));
      }
      if (property === 'patient') {
        return this.patientName(row).toUpperCase();
      }
      if (property === 'invoiceNumber') {
        return String(row?.invoiceNumber || '').toUpperCase();
      }
      if (property === 'vendor') {
        return this.vendorName(row).toUpperCase();
      }
      if (property === 'model') {
        return this.productField(row, 'model').toUpperCase();
      }
      return row?.[property];
    };
    this.load();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  load(): void {
    this.dataLoading = true;
    this.orderService.getNewOrders('job-ready').subscribe({
      next: (vendorOrders) => {
        const orders = (vendorOrders || []).flatMap(
          (vendorOrder) => vendorOrder.orders || []
        );
        this.orders = orders.sort((left, right) => {
          const rank =
            jobReadySortRank(this.statusLabel(left)) -
            jobReadySortRank(this.statusLabel(right));
          if (rank !== 0) {
            return rank;
          }
          return this.patientName(left).localeCompare(this.patientName(right));
        });
        this.dataSource.data = this.orders;
        this.orderService.setJobReadyCount(this.orders.length);
        this.dataLoading = false;
        if (this.sort) {
          this.dataSource.sort = this.sort;
        }
        this.cdRef.detectChanges();
      },
      error: () => {
        this.dataLoading = false;
      },
    });
  }

  patientName(order: any): string {
    const patient = order?.patient;
    if (!patient) {
      return '';
    }
    return `${patient.lastName || ''}, ${patient.firstName || ''}`.replace(
      /^,\s*|,\s*$/g,
      ''
    );
  }

  statusLabel(order: any): string {
    return jobReadyBoardStatus(order);
  }

  groupLabel(order: any): string {
    const summary = order?.linkSummary;
    if ((summary?.patientJobCount || 0) > 1) {
      return `${summary.patientJobIndex} OF ${summary.patientJobCount}`;
    }
    if (summary?.groupKind === 'FAMILY') {
      return 'FAM';
    }
    if (summary?.groupKind === 'HOUSEHOLD') {
      return 'HH';
    }
    if (summary?.groupKind) {
      return 'LNK';
    }
    return '';
  }

  vendorName(order: any): string {
    const vendor = order?.products?.[0]?.product?.vendor;
    if (!vendor) {
      return '';
    }
    return vendor.name || '';
  }

  productField(order: any, field: 'model' | 'color'): string {
    return order?.products?.[0]?.product?.[field] || '';
  }

  openOrder(order: any): void {
    const patientId = order?.patient?._id || order?.patient;
    if (!patientId || !order?._id) {
      return;
    }
    this.dialog
      .open(OrderEditDialogComponent, {
        width: '95vw',
        maxWidth: '1400px',
        data: { patientId, orderId: order._id },
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.load();
        }
      });
  }

  openNotify(order: any): void {
    this.dialog
      .open(CustomerNotifyDialogComponent, {
        width: '560px',
        data: { order },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.saved) {
          this.load();
        }
      });
  }
}
